import type { QuizAssignedDataWithAttempts } from '@repo/api/quiz/quizService';
import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { getBestQuizResponse } from '@repo/lib/quiz/getBestQuizResponse';
import { format, isAfter, isBefore } from 'date-fns';
import { ArrowUpRightIcon, LoaderIcon, MoreVerticalIcon, TimerIcon, TimerOffIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import QAttemptResultCard from '@/components/features/quizzes/QAttemptResultCard';
import { Badge } from '@/components/ui/badge';
import { Button, ButtonGroup } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useCredentials } from '@/store/auth-store';

interface QuizCardProps extends ComponentProps<'div'> {
  data: QuizAssignedDataWithAttempts;
  onStartCallback?: () => void;
}

enum AvailabilityState {
  Before,
  Available,
  After,
}

export default function QuizCard(quizCardProps: QuizCardProps) {
  const { data, onStartCallback, className, ...props } = quizCardProps;

  const availability: AvailabilityState = useMemo<AvailabilityState>(() => {
    const now = new Date();

    if (data.deadline && isAfter(now, data.deadline)) {
      return AvailabilityState.After;
    } else if (data.available && isAfter(now, data.available)) {
      return AvailabilityState.Available;
    } else if (data.available && isBefore(now, data.available)) {
      return AvailabilityState.Before;
    }
    return AvailabilityState.Available;
  }, [data]);

  const secondaryText = () => {
    switch (availability) {
      case AvailabilityState.Before:
        return (
          data.available && (
            <>
              <TimerIcon className="size-4" />
              {`Dostępny od: ${format(data.available, 'dd MMM yyyy')}`}
            </>
          )
        );
      case AvailabilityState.Available:
        return data.deadline ? (
          <>
            <TimerIcon className="size-4" />
            {`Wykonaj do: ${format(data.deadline, 'dd MMM yyyy')}`}
          </>
        ) : (
          <>
            <TimerOffIcon className="size-4" />
            {'Brak terminu'}
          </>
        );
      case AvailabilityState.After:
        return (
          data.deadline && (
            <>
              <TimerOffIcon className="size-4" />
              {`Wygasł o: ${format(data.deadline, 'dd MMM yyyy')}`}
            </>
          )
        );
    }
  };

  return (
    <div
      className={cn(
        'transition-outline border-border-primary bg-background outline-focus-ring flex h-fit flex-col rounded-2xl border outline-0 focus-visible:outline-3 disabled:opacity-75',
        className
      )}
      {...props}
    >
      <div className={'border-border-primary flex items-center gap-3 border-b p-4'}>
        <h5 className={'truncate text-lg font-semibold'}>{data.name}</h5>
      </div>
      <div className={'flex h-full flex-col gap-3 p-4'}>
        <div className="text-muted-foreground flex items-center gap-2 text-sm">{secondaryText()}</div>
        <div className="flex items-end justify-between">
          <div className="flex text-xs">
            <QuizCardAttemptData data={data} availability={availability} />
          </div>

          <QuizCardActionButton data={data} availability={availability} onStartQuiz={() => onStartCallback?.()} />
        </div>
      </div>
    </div>
  );
}

interface QuizCardAttemptDataProps {
  data: QuizAssignedDataWithAttempts;
  availability: AvailabilityState;
}
function QuizCardAttemptData(quizCardAttemptDataProps: QuizCardAttemptDataProps) {
  const { data, availability } = quizCardAttemptDataProps;
  const { lvl } = useCredentials();

  const uniqueUsersCount: number = useMemo<number>(() => new Set(data.quizresps.map((resp) => resp.userId)).size, [data.quizresps]);

  if (data.quizresps.length && lvl <= 0) {
    return <QAttemptResultCard score={getBestQuizResponse(data.quizresps)?.score ?? null} maxPoints={data.points} />;
  }

  if (lvl) {
    return <Badge variant={uniqueUsersCount > 0 ? 'blue' : 'muted'}>Wykonało: {uniqueUsersCount}</Badge>;
  }

  if (availability === AvailabilityState.Before) {
    return null;
  }

  if (availability === AvailabilityState.Available && !data.quizresps.length) {
    return (
      <Badge variant={'blue'}>
        <LoaderIcon />
        Wykonaj
      </Badge>
    );
  }
}

interface QuizCardActionButtonProps {
  data: QuizAssignedDataWithAttempts;
  availability: AvailabilityState;
  onStartQuiz: () => void;
}

function QuizCardActionButton(quizCardActionButtonProps: QuizCardActionButtonProps) {
  const { data, availability, onStartQuiz } = quizCardActionButtonProps;
  const navigate = useNavigate();
  const { lvl } = useCredentials();

  const isCompleted: boolean = data.quizresps.length > 0;
  const quizId: string = data.id;

  const disableStartQuiz: boolean = useMemo<boolean>(
    () => availability !== AvailabilityState.Available || (data.maxAttempts > 0 && data.quizresps.length >= data.maxAttempts),
    [availability, data.quizresps.length, data.maxAttempts]
  );

  if (lvl) {
    return (
      <Button variant={'outline'} onClick={() => navigate(`/manage/quizzes/${quizId}`)}>
        Podgląd
      </Button>
    );
  }

  if (isCompleted) {
    return (
      <ButtonGroup>
        <Button variant={'outline'} onClick={() => navigate(`/quizzes/${quizId}`)}>
          Podgląd
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant={'outline'}>
              <MoreVerticalIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={'end'}>
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate(`/quizzes/${quizId}`)}>Podgląd</DropdownMenuItem>
              <DropdownMenuItem disabled={disableStartQuiz} onClick={onStartQuiz}>
                Rozpocznij
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </ButtonGroup>
    );
  }

  return (
    <Button variant={'outline'} onClick={onStartQuiz}>
      Rozpocznij
      <ArrowUpRightIcon />
    </Button>
  );
}
